'use client';
import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import OnboardingTour from './OnboardingTour';

interface AppLayoutProps {
  children: React.ReactNode;
  userRole?: 'recovery' | 'volunteer' | 'admin';
}

export default function AppLayout({ children, userRole: propRole }: AppLayoutProps) {
  const [userRole, setUserRole] = useState<'recovery' | 'volunteer' | 'admin'>(propRole ?? 'recovery');
  const [userName, setUserName] = useState('Usuário');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // First try sessionStorage (legacy)
    try {
      const stored = sessionStorage.getItem('recomeço_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.role) setUserRole(parsed.role);
        if (parsed.name) setUserName(parsed.name);
        return;
      }
    } catch {}

    // Fall back to Supabase auth user metadata
    if (user) {
      const meta = user.user_metadata;
      const name = meta?.full_name || meta?.name || user.email?.split('@')[0] || 'Usuário';
      const role = (meta?.role as 'recovery' | 'volunteer' | 'admin') || 'recovery';
      setUserName(name);
      setUserRole(role);
    }
  }, [user]);

  return (
    <div className="flex h-screen bg-cream-50 overflow-hidden">
      {/* Onboarding Tour */}
      <OnboardingTour userId={user?.id} />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile unless mobileOpen */}
      <div
        className={`
          fixed inset-y-0 left-0 z-30 md:relative md:flex md:z-auto
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <Sidebar
          userRole={userRole}
          userName={userName}
          onClose={() => setMobileOpen(false)}
        />
      </div>

      <main className="flex-1 overflow-y-auto scrollbar-thin">
        {/* Mobile top bar with hamburger */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b md:hidden sticky top-0 z-10"
          style={{ backgroundColor: '#1A1A2E', borderColor: 'rgba(124,158,135,0.2)' }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: '#7C9E87' }}
            aria-label="Abrir menu"
          >
            <Menu size={22} />
          </button>
          <span className="text-sm font-semibold" style={{ color: '#F8F7F4' }}>1 Dia de Cada Vez</span>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 xl:px-10 py-6 md:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}